<template>
    
    <div>
        <div v-if="vendor">
            <h1>{{ vendor.name }}</h1>

            <div class="cover-container">
                <img v-if="vendor.coverUrl"
                     :src="vendor.coverUrl"
                     alt="Vendor Cover"
                     class="vendor-cover" />
                <div v-else class="vendor-cover fallback-cover"></div>

                <img v-if="vendor.logoUrl"
                     :src="vendor.logoUrl"
                     alt="Vendor Logo"
                     class="vendor-logo" />

                <div class="vendor-overlay">
                    <p v-if="vendor.deliveryFee !== null">
                        Delivery: &pound;{{ vendor.deliveryFee.toFixed(2) }}
                        <span v-if="vendor.deliveryFreeOrdersOver">
                            (Free over &pound;{{ vendor.deliveryFreeOrdersOver.toFixed(2) }})
                        </span>
                    </p>
                    <div v-if="googlePlaceDetailsMap[vendor.id]">
                        <small>
                            <i class="fab fa-google fa-lg me-2"></i>
                            <i class="fa fa-star" />
                            {{ googlePlaceDetailsMap[vendor.id].rating }}
                            ({{ googlePlaceDetailsMap[vendor.id].reviewCount }} reviews)
                        </small>
                    </div>
                    <small v-if="vendor.distanceFromCustomer">
                        {{ vendor.distanceFromCustomer.toFixed(1) }} miles away
                    </small>

                    <div v-if="availability?.isOpenNow">
                        <span class="badge bg-success">Open Now</span>
                    </div>
                    <div v-else>
                        <span class="badge bg-danger">Closed</span>
                        <small v-if="vendor.availability?.nextOpenUtc">
                            � Opens {{ new Date(availability.nextOpenUtc).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }}
                        </small>
                        <small v-if="vendor.availability?.closingReason">
                            � {{ availability.closingReason }}
                        </small>
                    </div>

                    <p v-if="vendor.foodHygieneRating > 0">
                        Food Hygiene Rating: {{ getHygieneLabel(vendor.foodHygieneRating) }}
                    </p>
                    <small v-if="vendor.restaurantCategoryName">
                        {{ vendor.restaurantCategoryName }}
                    </small>
                </div>
            </div>
            <div class="content my-3" v-if="vendor.description">
                {{vendor.description}}

            </div>
        </div>


        <!--<div class="text-end mb-3" v-if="availability">
            <span class="badge"
                  :class="availability.isOpenNow ? 'bg-success' : 'bg-danger'">
                {{ availability.isOpenNow ? 'Open Now' : 'Closed' }}
            </span>
            <span v-if="!availability.isOpenNow && availability.nextOpenUtc">
                &nbsp;Opens at {{ new Date(availability.nextOpenUtc).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }}
            </span>
        </div>-->
        <div v-if="loading">Loading items...</div>
        <div v-else>

            <h4 class="mb-2">What are you craving?</h4>

            <CategoryFilterBar :categories="allCategories"
                               :selected-ids="selectedCategories"
                               @toggle="toggleCategory" />

            <h4 class="mb-2 mt-4">Any dietary needs?</h4>

            <CategoryFilterBar :categories="allDietaryRequirements"
                               :selected-ids="selectedDietaryFilters"
                               @toggle="toggleDietaryFilter" />

            <small class="text-muted mt-1 d-block">
                Click to request specific dietary options. Blue ones are always included.
            </small>

            <div v-for="(categoryItems, categoryName) in filteredGroupedItems" :key="categoryName">


                <h3>{{ categoryName }}</h3>

                <div v-for="item in categoryItems" :key="item.id" class="menu-item">
                    <div class="item-info">
                        <img :src="item.imageUrl" v-if="item.imageUrl" class="item-image" />
                        <div class="item-details">
                            <h3>{{ item.itemName }}</h3>
                            <p class="description">{{ item.description }}</p>
                            <p v-if="item.calories !== null" class="calories">{{ item.calories }} kcal</p>
                            <div v-if="item.options?.length" class="item-options">
                                <div v-for="option in item.options" :key="option.id" class="option-group">
                                    <label>{{ option.name }}</label>
                                    <div class="option-choices">
                                        <label v-for="choice in option.choices"
                                               :key="choice.id"
                                               class="checkbox-label">
                                            <input type="checkbox"
                                                   :value="choice.id"
                                                   v-model="optionSelections[item.id][option.id]" />
                                            {{ choice.label }}
                                            <span class="price-adjust">
                                                (+&pound;{{ choice.priceAdjustment.toFixed(2) }})
                                            </span>
                                            <span class="calories" v-if="choice.calories">
                                                {{choice.calories}} kcal
                                            </span>

                                        </label>
                                        <p v-if="item.isSpicy" class="spicy-level" aria-label="Spice level">
                                            <i v-for="n in item.spiceLevel"
                                               :key="n"
                                               class="fas fa-pepper-hot chilli-icon"
                                               title="Spicy"
                                               aria-hidden="true"></i>
                                        </p>

                                    </div>
                                </div>
                            </div> 
                            <div v-if="item.dietaryRequirements?.length" class="dietary-tags mt-2">
                                <span v-for="req in item.dietaryRequirements"
                                      :key="req.name" 
                                      class="dietary-pill"
                                      :class="{
          'inherent': req.isInherent,
          'pill-selector': !req.isInherent,
          'selected': !req.isInherent && selectedDietaryPerItem[item.id]?.includes(req.name)
        }"
                                      :title="req.isInherent ? req.name + ' (always applies)' : 'Click to request this dietary preference'"
                                      @click="!req.isInherent && toggleDietarySelection(item.id, req.name)">
                                    <i v-if="req.icon?.startsWith('fa-')" :class="['fa-solid', req.icon]" aria-hidden="true"></i>
                                    {{ req.name }}
                                </span>
                            </div>

                        </div>
                    </div>
                    <div>
                        <p class="price">&pound;{{ item.price.toFixed(2) }}</p>

                        <div class="item-actions" :class="{ 'blurred': !canOrder }">
                            <div v-if="availability?.isOpenNow">
                                <button :disabled="!canOrder" v-if="getItemCount(item) === 0"
                                        class="add-button px-5"
                                        @click="addOne(item)">
                                    Add
                                </button>

                                <div v-else class="d-flex align-items-center gap-2">
                                    <div class="d-flex align-items-center gap-2">
                                        <button class="btn btn-outline-light btn-sm" @click="removeOne(item)"><i class="fa fa-minus"></i></button>
                                        <span>{{ getItemCount(item) }}</span>
                                        <button class="btn btn-outline-light btn-sm" @click="addOne(item)"><i class="fa fa-plus"></i></button>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="d-flex justify-content-end w-100">
                                <span class="badge bg-danger">Closed</span>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, reactive, computed, onMounted } from 'vue';
    import { useOrderStore } from '../../utils/orderStore';
    import { fetchWithAuth } from '../../utils/authFetch';

    import { useMedia } from '../../composables/useMedia'
    import { useHead } from '@vueuse/head'
    import CategoryFilterBar from '../Parts/CategoryFilterBar.vue'
    const { fetchVendorLogo, fetchVendorCover } = useMedia()

    const googlePlaceDetailsMap = ref({})

    const props = defineProps({
        vendorId: Number,
        onItemsSelected: Function
    });
    const getHygieneLabel = (value) => {
        const ratings = {
            1: 'Awaiting Inspection',
            2: 'Exempt',
            3: '0 - Urgent Improvement Necessary',
            4: '1 - Major Improvement Necessary',
            5: '2 - Improvement Necessary',
            6: '3 - Generally Satisfactory',
            7: '4 - Good',
            8: '5 - Very Good'
        }
        return ratings[value] || 'Not Set'
    }

    const loadGooglePlaceDetails = async () => {
        if (!vendors.value.length) return

        const vendorIds = vendors.value.map(v => v.id)
        const res = await fetch(`/PublicGooglePlace/batch-place-details-by-vendor`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(vendorIds)
        })

        if (res.ok) {
            const placeDetailsList = await res.json()
            googlePlaceDetailsMap.value = placeDetailsList.reduce((map, details) => {
                map[details.vendorId] = details
                return map
            }, {})
        }
    }
    const orderStore = useOrderStore();
    const selectedDietaryPerItem = reactive({});
    const items = ref([]);
    const quantities = reactive({});
    const optionSelections = reactive({});
    const availability = ref(null);
    const vendor = ref(null);
    const loading = ref(true);
    const selectedDietaryFilters = ref([]);
    const allCategories = computed(() =>
        [...new Set(items.value.map(item => item.categoryName || 'Uncategorised'))]
    );
    const selectedCategories = ref([]);
    function toggleCategory(cat) {
        if (selectedCategories.value.includes(cat)) {
            selectedCategories.value = selectedCategories.value.filter(c => c !== cat);
        } else {
            selectedCategories.value.push(cat);
        }
    }

    function toggleDietarySelection(itemId, name) {
        if (!selectedDietaryPerItem[itemId]) {
            selectedDietaryPerItem[itemId] = [];
        }

        const idx = selectedDietaryPerItem[itemId].indexOf(name);
        if (idx !== -1) {
            selectedDietaryPerItem[itemId].splice(idx, 1);
        } else {
            selectedDietaryPerItem[itemId].push(name);
        }
    }
    const groupedItems = computed(() => {
        const grouped = {};
        items.value.forEach(item => {
            const category = item.categoryName || '';
            if (!grouped[category]) grouped[category] = [];
            grouped[category].push(item);
        });
        return grouped;
    });

    const allDietaryRequirements = computed(() => {
        const unique = new Set();
        for (const item of items.value) {
            for (const dr of item.dietaryRequirements || []) {
                unique.add(dr.name);
            }
        }
        return [...unique].sort(); // optional sort for UX
    });

    function toggleDietaryFilter(name) {
        if (selectedDietaryFilters.value.includes(name)) {
            selectedDietaryFilters.value = selectedDietaryFilters.value.filter(n => n !== name);
        } else {
            selectedDietaryFilters.value.push(name);
        }
    }

    const filteredGroupedItems = computed(() => {
        const result = {};
        const categoriesToShow = selectedCategories.value.length > 0
            ? selectedCategories.value
            : allCategories.value;

        for (const [categoryName, items] of Object.entries(groupedItems.value)) {
            if (!categoriesToShow.includes(categoryName)) continue;

            const filtered = items.filter(item => {
                if (selectedDietaryFilters.value.length === 0) return true;
                return selectedDietaryFilters.value.every(req =>
                    item.dietaryRequirements?.some(i => i.name === req)
                );
            });

            if (filtered.length > 0) {
                result[categoryName] = filtered;
            }
        }

        return result;
    });
    function getSelectedOptions(itemId) {
        const rawOptions = optionSelections[itemId] || {};
        const selected = [];

        for (const optionId in rawOptions) {
            rawOptions[optionId].forEach(choiceId => {
                selected.push({ optionId: parseInt(optionId), choiceId });
            });
        }

        return selected;
    }

    function isSameCartItem(a, b) {
        if (a.itemId !== b.itemId) return false;
        if ((a.options?.length || 0) !== (b.options?.length || 0)) return false;
        if ((a.dietaryRequirements?.length || 0) !== (b.dietaryRequirements?.length || 0)) return false;

        const sort = (opts) => [...opts].sort((x, y) => x.optionId - y.optionId || x.choiceId - y.choiceId);
        const aSorted = sort(a.options || []);
        const bSorted = sort(b.options || []);
        const optionsMatch = aSorted.every((opt, i) =>
            opt.optionId === bSorted[i].optionId && opt.choiceId === bSorted[i].choiceId
        );

        const dietA = [...(a.dietaryRequirements || [])].sort();
        const dietB = [...(b.dietaryRequirements || [])].sort();
        const dietMatch = dietA.every((val, idx) => val === dietB[idx]);

        return optionsMatch && dietMatch;
    }


    function getItemCount(item) {
        const vendorId = vendor.value?.id;
        if (!vendorId) return 0;
        const selectedOptions = getSelectedOptions(item.id);
        const selectedDietary = selectedDietaryPerItem[item.id] || [];

        return orderStore.itemsByVendor[vendorId]?.filter(i =>
            i.itemId === item.id &&
            isSameCartItem(i, {
                itemId: item.id,
                options: selectedOptions,
                dietaryRequirements: selectedDietary
            })
        ).reduce((sum, i) => sum + i.quantity, 0) || 0;
    }

    function addOne(item) {
        const selectedOptions = getSelectedOptions(item.id);
        const selectedDietary = Array.from(selectedDietaryPerItem[item.id] || []);

        orderStore.addToCart({
            itemId: item.id,
            item,
            quantity: 1,
            options: selectedOptions,
            dietaryRequirements: selectedDietary
        });

        props.onItemsSelected?.();
    }

    function removeOne(item) {
        const vendorId = vendor.value?.id;
        if (!vendorId) return;

        const selectedOptions = getSelectedOptions(item.id);

        const selectedDietary = Array.from(selectedDietaryPerItem[item.id] || []);

        const matchingIndex = orderStore.itemsByVendor[vendorId]?.findIndex(i =>
            isSameCartItem(i, {
                itemId: item.id,
                options: selectedOptions,
                dietaryRequirements: selectedDietary
            })
        );

        if (matchingIndex !== -1) {
            const entry = orderStore.itemsByVendor[vendorId][matchingIndex];
            if (entry.quantity > 1) {
                entry.quantity--;
            } else {
                orderStore.itemsByVendor[vendorId].splice(matchingIndex, 1);
            }
            props.onItemsSelected?.();
        }
    }


    function removeItem(itemId) {
        orderStore.removeFromCart(itemId);
        props.onItemsSelected?.();
    }

    function addItem(itemId) {
        const qty = quantities[itemId];
        if (qty > 0) {
            const rawOptions = optionSelections[itemId];
            const selected = [];

            for (const optionId in rawOptions) {
                rawOptions[optionId].forEach(choiceId => {
                    selected.push({ optionId: parseInt(optionId), choiceId });
                });
            }

            const selectedItem = items.value.find(item => item.id === itemId);
            orderStore.addToCart({
                itemId,
                item: selectedItem,
                quantity: qty,
                options: selected
            });

            props.onItemsSelected?.();
        }
    }

    const customerLocation = ref(getStoredLocation())

    function getStoredLocation() {
        return JSON.parse(localStorage.getItem('customerLocation') || '{}')
    }
    const isCollection = computed(() => {
        const mode = customerLocation.value?.mode
        return mode === 'pickup'
    })

    const canOrder = computed(() => {
        console.log(customerLocation.value)
        if (!customerLocation || !customerLocation.value?.mode) {
            return false;

        }
        if (isCollection)
            return true;

        if (!customerLocation.address)
            return false;
        return true;
    });

    const headEntry = useHead({
        title: '',
        meta: [],
        link: [],
        script:[]
    })
    function removeEmptyFields(obj) {
        return JSON.parse(JSON.stringify(obj)); // strips undefined/null/"" fields
    }

    function setVendorMeta(vendor) {
        const structuredData = {
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "Restaurant",
                    "name": vendor.name,
                    "image": vendor.coverUrl || vendor.logoUrl,
                    "address": {
                        "@type": "PostalAddress",
                        "addressLocality": vendor.location?.town || '',
                        "postalCode": vendor.location?.postcode || '',
                        "addressCountry": "GB"
                    },
                    "mainEntityOfPage": {
                        "@type": "WebPage",
                        "@id": "https://tanm.co.uk/vendor/kebabybaba"
                    },
                    "servesCuisine": vendor.restaurantCategoryName,
                    "url": `https://tanm.co.uk/vendor/${vendor.slug}`,
                    "aggregateRating": {
                        "@type": "AggregateRating",
                        "ratingValue": googlePlaceDetailsMap.value[vendor.id]?.rating || 4.5,
                        "reviewCount": googlePlaceDetailsMap.value[vendor.id]?.reviewCount || 10
                    }
                },
                ...items.value.map(item => ({
                    "@type": "MenuItem",
                    "name": item.itemName,
                    "description": item.description || '',
                    "image": item.imageUrl || vendor.coverUrl || vendor.logoUrl,
                    "offers": {
                        "@type": "Offer",
                        "price": item.price.toFixed(2),
                        "priceCurrency": "GBP",
                        "availability": availability.value?.isOpenNow ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
                    },
                    ...(item.dietaryRequirements?.length ? {
                        "suitableForDiet": item.dietaryRequirements.map(req => {
                            // attempt to map to schema.org where possible
                            const map = {
                                "Vegetarian": "https://schema.org/VegetarianDiet",
                                "Vegan": "https://schema.org/VeganDiet",
                                "Gluten-Free": "https://schema.org/GlutenFreeDiet",
                                "Halal": "https://schema.org/HalalDiet",
                                "Kosher": "https://schema.org/KosherDiet"
                            };
                            return map[req.name] || req.name;
                        })
                    } : {})
                }))
            ]
        };
        const cleanedSchema = removeEmptyFields(structuredData);


        const title = `${vendor.name} - Order Online | TANM`
        const description = vendor.description || `Order from ${vendor.name} with TANM � secure checkout, live updates, and local delivery.`
        headEntry.patch({
            title,
            meta: [
                { name: 'description', content: description },
                { property: 'og:title', content: title },
                { property: 'og:description', content: description },
                { property: 'og:image', content: vendor.coverUrl || vendor.logoUrl || '/default-cover.jpg' },
                { property: 'og:type', content: 'restaurant' },
                { property: 'og:url', content: `https://tanm.co.uk/vendor/${vendor.slug}` },
                { name: 'twitter:card', content: 'summary_large_image' },
            ],
            link: [
                { rel: 'canonical', href: `https://tanm.co.uk/vendor/${vendor.slug}` }
            ],
            script: [
                {
                    type: 'application/ld+json',
                    children: JSON.stringify(structuredData)
                }
            ]
        });
    }

    onMounted(async () => {

        const [itemsRes, availabilityRes, vendorRes] = await Promise.all([
            fetch(`/publicvendor/${props.vendorId}/items`),
            fetch(`/publicvendor/${props.vendorId}/availability`),
            fetch(`/publicvendor/${props.vendorId}`)
        ]);

        vendor.value = await vendorRes.json();
        try {

            vendor.value.logoUrl = await fetchVendorLogo(vendor.value.id)
            vendor.value.coverUrl = await fetchVendorCover(vendor.value.id)
        } catch (err) {
            console.error('Error loading media for vendor', vendor.value.id, err)
            vendor.value.logoUrl = null
            vendor.value.coverUrl = null
        }


        items.value = await itemsRes.json();
        availability.value = await availabilityRes.json();

        items.value.forEach(item => {
            quantities[item.id] = 0;
            optionSelections[item.id] = {};

            selectedDietaryPerItem[item.id] = [];

            item.options?.forEach(option => {
                optionSelections[item.id][option.id] = [];
            });
        });
        setVendorMeta(vendor.value);

        loading.value = false;
    });
</script>


<style scoped>
    .menu-container {
        padding: 1rem;
        max-width: 800px;
        margin: auto;
    }

    .menu-item {
        background: #25003e;
        padding: 1rem;
        border-radius: 10px;
        margin-bottom: 1.5rem;
        display: flex;
        flex-direction: column;
        gap: 1rem;
        overflow: hidden;
    }

    .item-info {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    @media (min-width: 600px) {
        .menu-item {
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
        }

        .item-info {
            flex-direction: row;
            align-items: flex-start;
        }

        .item-actions {
            width: 150px;
            align-items: flex-end;
        }
    }

    .item-image {
        width: 80px;
        height: 80px;
        object-fit: cover;
        border-radius: 8px;
    }

    .item-details {
        flex: 1;
    }

    .description {
        color: #bbb;
        margin: 0.25rem 0;
    }

    .price {
        color: #0ff;
        font-weight: bold;
        justify-content: center;
        display: flex;
    }

    .item-options {
        margin-top: 0.5rem;
    }

    .option-group {
        margin-bottom: 0.5rem;
    }

    .option-choices {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
    }

    .checkbox-label {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.9rem;
    }

    .price-adjust {
        color: #aaa;
    }

    .item-actions {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        align-items: flex-start;
    }

    .quantity-input {
        width: 100%;
        padding: 10px; 
        border-radius: 4px;
        text-align: center;
        border: 1px solid #aaa;
        box-sizing: border-box;
    }

    .add-button {
        width: 100%;
        background-color: #118ab2;
        color: white;
        font-weight: bold;
        padding: 10px;
        border: none;
        border-radius: 6px;
        cursor: pointer;
    }

        .add-button:hover {
            background-color: #0d6b8b;
        }

    .calories {
        font-size: 0.9rem;
        color: #8fd19e;
        font-weight: 500;
        margin-top: 0.25rem;
    }

    .spicy-level {
        margin-top: 0.25rem;
        color: #e74c3c;
        font-size: 1.1rem;
    }

    .chilli-icon {
        margin-right: 2px;
    }

    .dietary-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 0.4rem;
    }

    .dietary-pill {
        background-color: #444;
        color: #fff;
        font-size: 0.75rem;
        padding: 4px 8px;
        border-radius: 12px;
        display: inline-flex;
        align-items: center;
        gap: 4px;
    }

        .dietary-pill.inherent {
            background-color: #00b894; /* teal for inherent */
            font-weight: bold;
        }

    .blurred {
        filter: blur(2px);
        pointer-events: none;
        opacity: 0.5;
    }

    .overlay-hint {
        position: fixed;
        top: 100px;
        left: 0;
        right: 0;
        background: #fff3cd;
        color: #856404;
        padding: 1rem;
        text-align: center;
        z-index: 1000;
        border: 1px solid #ffeeba;
    }

    .menu-item {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: stretch;
    }

    .item-info {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .item-actions {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
        margin-top: 1rem;
    }

    @media (min-width: 600px) {
        .menu-item {
            flex-direction: row;
            align-items: center;
        }

        .item-info {
            flex-direction: row;
            align-items: flex-start;
        }

        .item-actions {
            margin-top: 0;
            width: 150px;
            align-items: center;
            justify-content: center;
        }
    }
    .pill-selector{
        cursor:pointer;
    }
    .dietary-pill.selected {
        background-color: #095e6b;
        color: #fff;
        border: 1px solid #1d3557;
    }

</style>
